/**
 * PlayerStats: vanilla survival rules for the player — health, hunger /
 * saturation / exhaustion, air, damage (armor, i-frames, fall / fire / lava /
 * cactus / void), XP levels, death and respawn. DOM-free, ticked at 20 Hz.
 *
 * Wiring (lead):
 *   const stats = new PlayerStats({ getArmor, onHurt, onDeath, onHeal });
 *   every tick: stats.addMovement(player.movementExhaustion());
 *               stats.tick({ mode, inWater, headInWater, inLava, onGround, sprinting, pos });
 *               player.setSprintAllowed(stats.canSprint);
 *   landing:    player.onFall = (d) => stats.fall(d);
 *   attack/break/jump costs: stats.addExhaustion(...) (see EXHAUST_*).
 *   death:      onDeath -> set player.dead, show menu; XP to drop: stats.xpDropOnDeath().
 *   respawn:    stats.respawn(); player.teleportTo(...).
 *
 * Deviation from the doc: i-frames are vanilla's 20 ticks (`hurtResistantTime`),
 * with the second half (<= 10 left) re-hittable by anything stronger — i.e.
 * damage lands every 10 ticks at best, and a bigger hit inside the window only
 * deals the difference.
 */

import type { DamageKind, DamageSource, GameMode } from './types';
import { applyTotemEffects } from './itemUse/totem';
import { EffectSet, FX, absorptionCap, damageTakenMult, fireImmune, waterBreathing, type EffectTarget } from './effects/effects';

export const MAX_HEALTH = 20;
export const MAX_AIR = 300;
/** Below this y the void hurts (the world is 0..191 here). */
export const VOID_Y = -20;

/** Vanilla exhaustion costs. */
export const EXHAUST_WALK_SWIM = 0.01;   // per metre in water
export const EXHAUST_SPRINT = 0.1;       // per metre
export const EXHAUST_JUMP = 0.05;
export const EXHAUST_SPRINT_JUMP = 0.2;
export const EXHAUST_ATTACK = 0.1;
export const EXHAUST_BREAK = 0.005;
export const EXHAUST_DAMAGE = 0.1;
export const EXHAUST_REGEN = 6;

/** Damage kinds armor does not reduce (vanilla `bypasses_armor`). */
const BYPASS_ARMOR: ReadonlySet<DamageKind> = new Set(['fall', 'drown', 'starve', 'void', 'generic', 'magic', 'sonic_boom']);

/** What the player's own movement cost since the last call (Player.movementExhaustion()). */
export interface MovementDelta {
  /** Metres walked on the ground (free in vanilla; kept for completeness). */
  walked: number;
  sprinted: number;
  /** Metres moved in water. */
  swum: number;
  jumps: number;
  sprintJumps: number;
}

export interface TickContext {
  mode: GameMode;
  inWater: boolean;
  headInWater: boolean;
  inLava: boolean;
  onGround: boolean;
  sprinting: boolean;
  pos: { x: number; y: number; z: number };
  /** Standing in a fire block (sets you alight, 1 dmg / 10 ticks while inside). */
  inFire?: boolean;
  /** Touching a cactus (1 dmg, i-frame gated). */
  touchingCactus?: boolean;
  /** Respiration level of the helmet (0..3). */
  respiration?: number;
  /** Peaceful difficulty: health regenerates, hunger does not drain. */
  difficultyPeaceful?: boolean;
}

export interface PlayerStatsHost {
  /** Total armor points (0..20) and toughness of the worn pieces. */
  getArmor: () => { defense: number; toughness: number };
  /** Fired for every point of damage actually applied (after armor / i-frames). */
  onHurt?: (amount: number, src: DamageSource) => void;
  onDeath?: (src: DamageSource) => void;
  /** A lethal hit: consume a held totem of undying and play its pop; true = death cancelled. */
  tryTotem?: (src: DamageSource) => boolean;
  onHeal?: (amount: number) => void;
  /** Total enchantment protection factor (EPF) of the worn armor against `kind` (capped at 20 here). */
  getEnchantProtection?: (kind: DamageKind) => number;
}

/** Total XP needed to *reach* `level` from 0 (vanilla). */
export function xpForLevel(level: number): number {
  if (level <= 16) return level * level + 6 * level;
  if (level <= 31) return 2.5 * level * level - 40.5 * level + 360;
  return 4.5 * level * level - 162.5 * level + 2220;
}
/** XP bar span of one level (vanilla `getXpNeededForNextLevel`). */
export function xpNeededForNext(level: number): number {
  if (level >= 30) return 112 + (level - 30) * 9;
  if (level >= 15) return 37 + (level - 15) * 5;
  return 7 + level * 2;
}

export interface PlayerStatsSave {
  health: number; food: number; saturation: number; exhaustion: number; air: number;
  xpTotal: number; xpLevel: number; xpProgress: number; score: number; fire: number;
  /** EffectSet.serialize() and current absorption hearts (health points). */
  effects?: number[]; absorption?: number;
}

export class PlayerStats {
  health = MAX_HEALTH;
  readonly maxHealth = MAX_HEALTH;
  food = 20;
  saturation = 5;
  exhaustion = 0;
  private airRaw = MAX_AIR;
  readonly maxAir = MAX_AIR;

  xpTotal = 0;
  xpLevel = 0;
  /** 0..1 progress through the current level. */
  xpProgress = 0;
  score = 0;

  dead = false;
  /** Ticks since death, capped at 20 (the death animation length). */
  deathTime = 0;
  /** Ticks of fire left (20 ticks = 1 damage). */
  fire = 0;
  /** Red hurt flash ticks, 10 -> 0 (for the HUD). */
  hurtTime = 0;
  /** i-frames left (vanilla `invulnerableTime`, 20 after a hit). */
  invulnTime = 0;
  /** Post-armor amount of the last hit, for the stronger-hit-in-window rule. */
  private lastDamage = 0;
  private foodTimer = 0;
  tickCount = 0;
  /** Ticks since the player last slept in a bed or died (phantoms spawn after 72000). */
  timeSinceRest = 0;
  /** Active status effects (game/effects). */
  readonly effects = new EffectSet();
  /** Absorption health points shielding the hearts (Absorption effect: 4 per level). */
  absorption = 0;

  /** View of the player for EffectSet.tick / applyInstant (poison itself stops at 1 HP in EffectSet). */
  readonly effectTarget: EffectTarget;

  constructor(private readonly host: PlayerStatsHost) {
    const self = this;
    this.effectTarget = {
      get health(): number { return self.health; },
      maxHealth: MAX_HEALTH,
      heal: (n) => this.heal(n),
      hurt: (n) => { this.damage(n, { kind: 'magic' }); },
      exhaust: (n) => this.addExhaustion(n),
    };
    this.effects.listener = (e, added): void => {
      if (e.id !== FX.ABSORPTION) return;
      this.absorption = added ? Math.max(this.absorption, absorptionCap(this.effects)) : 0;
    };
  }

  /** Air supply for the HUD, 0..300 (bubbles drawn from this). */
  get air(): number { return Math.max(0, this.airRaw); }
  /** Hunger bar low enough to forbid sprinting (vanilla: food <= 6). */
  get canSprint(): boolean { return this.food > 6; }
  get lowHealth(): boolean { return this.health <= 4; }
  /** 0..1 for the red vignette. */
  get hurtFlash(): number { return this.hurtTime / 10; }

  /** Armor reduction factor applied to `amount` (vanilla `CombatRules.getDamageAfterAbsorb`). */
  static reduce(amount: number, defense: number, toughness: number): number {
    const f = Math.min(20, Math.max(defense / 5, defense - (4 * amount) / (toughness + 8)));
    return amount * (1 - f / 25);
  }

  /**
   * Apply damage. Returns the health actually removed (0 when blocked by
   * creative mode / i-frames). `mode` is tracked from the last tick().
   */
  damage(amount: number, src: DamageSource): number {
    if (this.dead || amount <= 0) return 0;
    const kind = src.kind;
    const isVoid = kind === 'void';
    if (this.creative && !isVoid) return 0;
    if ((kind === 'fire' || kind === 'lava') && fireImmune(this.effects)) return 0;
    if (kind === 'drown' && waterBreathing(this.effects)) return 0;
    if (!BYPASS_ARMOR.has(kind)) {
      const a = this.host.getArmor();
      if (a.defense > 0) amount = PlayerStats.reduce(amount, a.defense, a.toughness);
    }
    // enchantment protection: 4% per EPF point, capped at 20 (80%); void/starvation bypass it
    if (kind !== 'void' && kind !== 'starve' && kind !== 'sonic_boom') {
      const epf = this.host.getEnchantProtection?.(kind) ?? 0;
      if (epf > 0) amount *= 1 - Math.min(20, epf) / 25;
    }
    amount *= damageTakenMult(this.effects, kind);
    if (amount <= 0) return 0;
    let applied = amount;
    if (!isVoid && this.invulnTime > 10) {
      // inside the i-frame window only the excess of a stronger hit lands
      applied = amount - this.lastDamage;
      if (applied <= 0) return 0;
      this.lastDamage = amount;
    } else {
      this.lastDamage = amount;
      this.invulnTime = 20;
      this.hurtTime = 10;
    }
    if (this.absorption > 0 && !isVoid) {
      // Absorption hearts take the hit first
      const soak = Math.min(this.absorption, applied);
      this.absorption -= soak;
      applied -= soak;
    }
    this.health = Math.max(0, this.health - applied);
    if (!this.creative) this.addExhaustion(EXHAUST_DAMAGE);
    this.host.onHurt?.(applied, src);
    if (this.health <= 0 && !isVoid && this.host.tryTotem?.(src)) {
      this.health = 1;
      applyTotemEffects(this.effects);
      return applied;
    }
    if (this.health <= 0) this.die(src);
    return applied;
  }

  /** Landing after a fall of `distance` blocks: vanilla ceil(distance - 3). Returns damage dealt. */
  fall(distance: number): number {
    const dmg = Math.ceil(distance - 3);
    return dmg > 0 ? this.damage(dmg, { kind: 'fall' }) : 0;
  }

  heal(n: number): void {
    if (this.dead || n <= 0) return;
    const before = this.health;
    this.health = Math.min(MAX_HEALTH, this.health + n);
    if (this.health > before) this.host.onHeal?.(this.health - before);
  }

  /** Eat food: `hunger` points, `saturation` as the item table's saturation value (apple 2.4). */
  eat(hunger: number, saturation: number): void {
    this.food = Math.min(20, this.food + hunger);
    this.saturation = Math.min(this.food, this.saturation + saturation);
  }

  addExhaustion(x: number): void {
    if (this.creative) return;
    this.exhaustion = Math.min(40, this.exhaustion + x);
  }

  /** Charge the cost of the movement Player.movementExhaustion() reported. */
  addMovement(m: MovementDelta): void {
    this.addExhaustion(m.swum * EXHAUST_WALK_SWIM + m.sprinted * EXHAUST_SPRINT
      + m.jumps * EXHAUST_JUMP + m.sprintJumps * EXHAUST_SPRINT_JUMP);
  }

  /** Light the player: fire ticks only ever go up (vanilla `setRemainingFireTicks`). */
  setOnFire(ticks: number): void {
    if (ticks > this.fire) this.fire = ticks;
  }
  get onFire(): boolean { return this.fire > 0; }

  addXp(points: number): void {
    if (points <= 0 || this.dead) return;
    this.score += points;
    this.xpTotal += points;
    // walk the levels in whole points (no float drift at level boundaries)
    let pts = points;
    while (pts > 0) {
      const need = xpNeededForNext(this.xpLevel);
      const room = need - this.xpProgress * need;
      if (pts >= room - 1e-9) { pts -= room; this.xpLevel++; this.xpProgress = 0; }
      else { this.xpProgress += pts / need; pts = 0; }
    }
  }

  /** Pay `n` levels (enchanting / anvil); progress within the level is kept like vanilla. */
  spendLevels(n: number): void {
    if (n <= 0) return;
    this.xpLevel = Math.max(0, this.xpLevel - n);
    this.xpTotal = Math.max(0, Math.floor(xpForLevel(this.xpLevel) + this.xpProgress * xpNeededForNext(this.xpLevel)));
  }

  /** XP the corpse drops (vanilla min(level*7, 100)). */
  xpDropOnDeath(): number { return Math.min(this.xpLevel * 7, 100); }

  private mode: GameMode = 'survival';
  private get creative(): boolean { return this.mode === 'creative'; }

  private die(src: DamageSource): void {
    this.dead = true;
    this.deathTime = 0;
    this.fire = 0;
    this.host.onDeath?.(src);
  }

  /** Full reset for a new life. */
  respawn(): void {
    this.health = MAX_HEALTH; this.food = 20; this.saturation = 5; this.exhaustion = 0;
    this.airRaw = MAX_AIR;
    this.xpTotal = 0; this.xpLevel = 0; this.xpProgress = 0; this.score = 0;
    this.dead = false; this.deathTime = 0; this.fire = 0; this.hurtTime = 0;
    this.timeSinceRest = 0;
    this.invulnTime = 0; this.lastDamage = 0; this.foodTimer = 0;
    this.effects.clear(); this.absorption = 0;
  }

  /** One 20 Hz tick. */
  tick(ctx: TickContext): void {
    this.tickCount++;
    if (!this.dead) this.timeSinceRest++;
    this.mode = ctx.mode;
    if (this.dead) {
      if (this.deathTime < 20) this.deathTime++;
      return;
    }
    if (this.invulnTime > 0) this.invulnTime--;
    if (this.hurtTime > 0) this.hurtTime--;
    const creative = this.creative;

    // void: 4 per tick, ignores creative and i-frames
    if (ctx.pos.y < VOID_Y) this.damage(4, { kind: 'void' });
    if (this.dead) return;

    // --- air / drowning ---
    if (ctx.headInWater && !creative && !waterBreathing(this.effects)) {      // Respiration: lvl/(lvl+1) chance per tick to keep the air
      const r = ctx.respiration ?? 0;
      if (!(r > 0 && Math.random() < r / (r + 1))) this.airRaw--;
      if (this.airRaw <= -20) {
        this.airRaw = 0;
        this.damage(2, { kind: 'drown' });
      }
    } else if (ctx.headInWater) {
      // creative: never drowns, air frozen; Water Breathing keeps the bar full
      if (waterBreathing(this.effects)) this.airRaw = MAX_AIR;
    } else {
      this.airRaw = Math.min(MAX_AIR, Math.max(0, this.airRaw) + 4);
    }
    if (this.dead) return;

    // --- fire / lava / cactus ---
    if (ctx.inWater) this.fire = 0;
    if (ctx.inLava) {
      this.setOnFire(300);
      this.damage(4, { kind: 'lava' }); // i-frames throttle this to one hit per 10 ticks
    }
    if (ctx.inFire) {
      this.setOnFire(160);
    }
    if (this.fire > 0) {
      if (this.fire % 20 === 0) this.damage(1, { kind: 'fire' });
      this.fire--;
    }
    if (ctx.touchingCactus) this.damage(1, { kind: 'cactus' });
    if (this.dead) return;

    // --- status effects (regeneration / poison / wither / hunger tick here; Absorption fades with its effect) ---
    if (this.effects.size > 0) this.effects.tick(this.effectTarget);
    if (this.absorption > 0 && !this.effects.has(FX.ABSORPTION)) this.absorption = 0;
    if (this.dead) return;
    if (creative) return;

    // --- hunger ---
    if (ctx.difficultyPeaceful) {
      if (this.health < MAX_HEALTH && this.tickCount % 20 === 0) this.heal(1);
      this.food = Math.max(this.food, 20);
    }
    if (this.exhaustion >= 4) {
      this.exhaustion -= 4;
      if (this.saturation > 0) this.saturation = Math.max(0, this.saturation - 1);
      else if (this.food > 0) this.food--;
    }
    const hurt = this.health < MAX_HEALTH && this.health > 0;
    if (hurt && this.saturation > 0 && this.food >= 20) {
      if (++this.foodTimer >= 10) {
        const f = Math.min(this.saturation, 6);
        this.heal(f / 6);
        this.addExhaustion(f);
        this.foodTimer = 0;
      }
    } else if (hurt && this.food >= 18) {
      if (++this.foodTimer >= 80) {
        this.heal(1);
        this.addExhaustion(EXHAUST_REGEN);
        this.foodTimer = 0;
      }
    } else if (this.food <= 0) {
      // starvation (normal difficulty): 1 HP / 80 ticks, stops at half a heart
      if (++this.foodTimer >= 80) {
        if (this.health > 1) this.damage(1, { kind: 'starve' });
        this.foodTimer = 0;
      }
    } else {
      this.foodTimer = 0;
    }
  }

  serialize(): PlayerStatsSave {
    return {
      health: this.health, food: this.food, saturation: this.saturation,
      exhaustion: this.exhaustion, air: this.airRaw, xpTotal: this.xpTotal,
      xpLevel: this.xpLevel, xpProgress: this.xpProgress, score: this.score, fire: this.fire,
      effects: this.effects.serialize(), absorption: this.absorption,
    };
  }

  load(s: Partial<PlayerStatsSave> | null | undefined): void {
    if (!s) return;
    const n = (v: unknown, d: number): number => (typeof v === 'number' && Number.isFinite(v) ? v : d);
    this.health = Math.min(MAX_HEALTH, Math.max(0, n(s.health, MAX_HEALTH)));
    this.food = Math.min(20, Math.max(0, n(s.food, 20)));
    this.saturation = Math.min(this.food, Math.max(0, n(s.saturation, 5)));
    this.exhaustion = Math.max(0, n(s.exhaustion, 0));
    this.airRaw = Math.min(MAX_AIR, n(s.air, MAX_AIR));
    this.xpTotal = Math.max(0, n(s.xpTotal, 0));
    this.xpLevel = Math.max(0, Math.floor(n(s.xpLevel, 0)));
    this.xpProgress = Math.min(0.999, Math.max(0, n(s.xpProgress, 0)));
    this.score = Math.max(0, n(s.score, 0));
    this.fire = Math.max(0, n(s.fire, 0));
    this.effects.load(s.effects);
    this.absorption = Math.min(absorptionCap(this.effects), Math.max(0, n(s.absorption, 0)));
    this.dead = this.health <= 0;
    this.deathTime = this.dead ? 20 : 0;
    this.invulnTime = 0; this.hurtTime = 0; this.foodTimer = 0;
  }
}
